import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { api, tokenStore } from "./api";

const AuthContext = createContext(null);


/**
 * Fire a silent "maybe-sync" call after an admin OR pre-recruteur logs in /
 * refreshes their session.
 *
 * Backend keeps a 60-minute SHARED cooldown so repeated calls cost ~5 ms each
 * regardless of who triggers them. The actual Layan fetch runs in the
 * background, so this NEVER blocks the UX. If the call fails (network /
 * token / role), we silently swallow — the scheduled auto_sync (every 5 min)
 * is the safety net.
 */
async function maybeSyncIfAllowed(u) {
    if (!u || (u.role !== "admin" && u.role !== "pre_recruteur")) return;
    try {
        await api.post("/integrations/layan/maybe-sync");
    } catch (_e) {
        // intentional silent fail — non-critical background hint
    }
}


export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    // Track which user IDs have already triggered a maybe-sync this session
    // so a page refresh doesn't spam the endpoint. Backend cooldown is the
    // real source of truth, this is just to avoid pointless round-trips.
    const syncedUserIds = useRef(new Set());

    const refresh = useCallback(async () => {
        const token = tokenStore.get();
        if (!token) {
            setUser(null);
            setLoading(false);
            return null;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            if (data?.id && !syncedUserIds.current.has(data.id)) {
                syncedUserIds.current.add(data.id);
                maybeSyncIfAllowed(data);
            }
            return data;
        } catch {
            tokenStore.clear();
            setUser(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        tokenStore.set(data.access_token);
        setUser(data.user);
        if (data.user?.id) syncedUserIds.current.add(data.user.id);
        // Fire-and-forget — no await so the UI navigates to the cockpit
        // immediately while the sync happens server-side.
        maybeSyncIfAllowed(data.user);
        return data.user;
    }, []);

    const logout = useCallback(() => {
        tokenStore.clear();
        setUser(null);
        syncedUserIds.current.clear();
    }, []);

    const value = useMemo(
        () => ({ user, loading, login, logout, refresh }),
        [user, loading, login, logout, refresh],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
